import React, { useState, useEffect, useCallback } from 'react';
import Toast from './Toast';
import IdeaModal from './IdeaModal';
import ReportViewer from './ReportViewer';
import useToast from '../hooks/useToast';

/**
 * Layer 2: Artikel-Ideen aus der Synthese — Kanban nach Status.
 *
 * UX-Sprint: Die Karte ist bewusst KOMPAKT (Titel, Hook, Badges,
 * Pipeline-Fortschritt, EIN "Nächster Schritt"-Button). Alles Weitere
 * (Inhalte, Quellen, Repurpose, Status-Wechsel, Löschen) lebt im
 * IdeaModal — vorher quetschten sich ~14.000 Zeichen in die 260px-Spalte.
 */
const STATUS_META = {
  offen:       { label: 'Offen',       cls: 'idea-col-offen',   icon: '○',  desc: 'Frisch aus der Recherche-Synthese — noch nicht gesichtet.' },
  ausgewaehlt: { label: 'Ausgewählt',  cls: 'idea-col-sel',     icon: '★',  desc: 'Von dir mit ★ gewählt — bereit für die Gliederung 📝.' },
  in_arbeit:   { label: 'In Arbeit',   cls: 'idea-col-work',    icon: '✎',  desc: 'Gliederung 📝 erstellt — nächster Schritt: Entwurf 📄.' },
  entwurf:     { label: 'Entwurf',     cls: 'idea-col-draft',   icon: '📄', desc: 'Entwurf 📄 fertig — Faktencheck 🔬, dann verteilen.' },
  verworfen:   { label: 'Verworfen',   cls: 'idea-col-trash',   icon: '✕',  desc: 'Aussortiert — nicht weiterverfolgt.' },
};
const STATUS_ORDER = ['offen', 'ausgewaehlt', 'in_arbeit', 'entwurf', 'verworfen'];

// Pipeline-Fortschritt auf der Karte: welche Artefakte existieren?
const PROGRESS_STEPS = [
  { key: 'outline',    icon: '📝', label: 'Gliederung',  has: i => !!i.outline,        gen: i => i.outline_status === 'generating' },
  { key: 'draft',      icon: '📄', label: 'Entwurf',     has: i => !!i.draft,          gen: i => i.draft_status === 'generating' },
  { key: 'factcheck',  icon: '🔬', label: 'Faktencheck', has: i => !!i.fact_check,     gen: i => i.fact_check_status === 'generating' },
  { key: 'ghost',      icon: '👻', label: 'Ghost',       has: i => !!i.ghost_post_id,  gen: i => i.ghost_status === 'pushing' },
  { key: 'video',      icon: '🎬', label: 'Video',       has: i => !!i.revid_video_url, gen: i => i.revid_status === 'rendering' },
];

// Kontextabhängiger Haupt-Button: der jeweils sinnvolle nächste Schritt.
function nextStep(idea) {
  const st = idea.status || 'offen';
  if (st === 'verworfen') return { key: 'reopen',    label: '○ Reaktivieren',  title: 'Zurück nach „Offen"' };
  if (st === 'offen')     return { key: 'select',    label: '★ Auswählen',     title: 'Für die Weiterbearbeitung markieren' };
  // Audit v3: Repurpose/Video-Skript ist ebenfalls ein lokaler LLM-Lauf — solange er
  // läuft, darf die Karte keinen weiteren schweren Schritt anbieten (Lastschutz).
  if (idea.repurpose_status === 'generating')  return { busy: true, label: '⏳ Repurpose…' };
  if (idea.outline_status === 'generating')    return { busy: true, label: '⏳ Gliederung…' };
  if (!idea.outline)      return { key: 'outline',   label: '📝 Gliederung',   title: 'Redaktions-Gliederung erstellen (1–3 min)' };
  if (idea.draft_status === 'generating')      return { busy: true, label: '⏳ Entwurf…' };
  if (!idea.draft)        return { key: 'draft',     label: '📄 Entwurf',      title: 'Artikel-Entwurf schreiben (2–4 min)' };
  if (idea.fact_check_status === 'generating') return { busy: true, label: '⏳ Faktencheck…' };
  if (!idea.fact_check)   return { key: 'factcheck', label: '🔬 Faktencheck',  title: 'Entwurf gegen die Quellen prüfen (1–3 min)' };
  if (!idea.ghost_post_id) return { key: 'ghost',    label: '👻 Nach Ghost',   title: 'Als Draft ins CMS — veröffentlicht wird manuell' };
  return { key: 'open', label: '♻ Verteilen', title: 'Repurpose-Kanäle & Details öffnen' };
}

export default function IdeasView({ authToken, onLogout, onDiscussIdea, onHelp, openIdeaId: externalOpenId, onOpenIdeaConsumed }) {
  const [ideas,      setIdeas]      = useState([]);
  const [openIdeaId, setOpenIdeaId] = useState(null);   // Detail-Modal
  const [viewReport, setViewReport] = useState(null);   // Quell-Report-Viewer
  const [busy,       setBusy]       = useState('');
  const [view,       setView]       = useState('board'); // 'board' | 'dist' (Verteilt)
  const [dist,       setDist]       = useState(null);    // Verteil-Protokoll
  const [toasts,     pushToast]     = useToast();

  const auth = { Authorization: `Bearer ${authToken}` };

  const loadIdeas = useCallback(() => {
    fetch('/api/ideas', { headers: auth })
      .then(r => {
        if (r.status === 401) { pushToast('Sitzung abgelaufen', 'error'); onLogout?.(); return null; }
        return r.json();
      })
      .then(d => { if (d) setIdeas(d.ideas || []); })
      .catch(() => pushToast('Ideen konnten nicht geladen werden', 'error'));
  }, [authToken, onLogout]);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  // Von außen (Posting-Tab) angefordertes Ideen-Modal öffnen, sobald Ideen geladen sind
  useEffect(() => {
    if (externalOpenId && ideas.some(i => i.id === externalOpenId)) {
      setOpenIdeaId(externalOpenId);
      onOpenIdeaConsumed?.();
    }
  }, [externalOpenId, ideas, onOpenIdeaConsumed]);

  // Solange irgendein Aufbereitungs-Job läuft, alle 6s neu laden.
  // Audit v3: pollTick re-armiert den Timer auch nach fehlgeschlagenem Fetch —
  // vorher fror „⏳ läuft…" nach einem transienten Netzfehler bis zum manuellen ↻ ein.
  const [pollTick, setPollTick] = useState(0);
  useEffect(() => {
    const anyBusy = ideas.some(i =>
      i.outline_status === 'generating' || i.draft_status === 'generating' ||
      i.fact_check_status === 'generating' || i.repurpose_status === 'generating' ||
      i.revid_status === 'rendering');
    if (!anyBusy) return;
    const t = setTimeout(() => { loadIdeas(); setPollTick(x => x + 1); }, 6000);
    return () => clearTimeout(t);
  }, [ideas, pollTick, loadIdeas]);

  const generateOutline = async (id) => {
    setBusy(`o-${id}`);
    const r = await fetch(`/api/ideas/${id}/outline`, { method: 'POST', headers: auth }).catch(() => null);
    if (r?.ok || r?.status === 202) {
      pushToast('Gliederung wird erstellt (1–3 min)…', 'info');
      loadIdeas();
    } else if (r?.status === 409) {
      const d = await r.json().catch(() => ({}));
      pushToast(d.error || 'Läuft bereits — Layer 3 arbeitet sequenziell', 'info');
    } else {
      pushToast('Gliederung konnte nicht gestartet werden', 'error');
    }
    setBusy('');
  };

  const generateDraft = async (id) => {
    setBusy(`d-${id}`);
    const r = await fetch(`/api/ideas/${id}/draft`, { method: 'POST', headers: auth }).catch(() => null);
    if (r?.ok || r?.status === 202) {
      pushToast('Artikel-Entwurf wird geschrieben (2–4 min)…', 'info');
      loadIdeas();
    } else if (r?.status === 409 || r?.status === 400) {
      const d = await r.json().catch(() => ({}));
      pushToast(d.error || 'Entwurf nicht möglich', 'info');
    } else {
      pushToast('Entwurf konnte nicht gestartet werden', 'error');
    }
    setBusy('');
  };

  const generateFactCheck = async (id) => {
    setBusy(`f-${id}`);
    const r = await fetch(`/api/ideas/${id}/factcheck`, { method: 'POST', headers: auth }).catch(() => null);
    if (r?.ok || r?.status === 202) {
      pushToast('Faktencheck läuft (1–3 min)…', 'info');
      loadIdeas();
    } else if (r?.status === 409 || r?.status === 400) {
      const d = await r.json().catch(() => ({}));
      pushToast(d.error || 'Faktencheck nicht möglich', 'info');
    } else {
      pushToast('Faktencheck konnte nicht gestartet werden', 'error');
    }
    setBusy('');
  };

  const pushToGhost = async (id) => {
    setBusy(`g-${id}`);
    const r = await fetch(`/api/ideas/${id}/ghost`, { method: 'POST', headers: auth }).catch(() => null);
    const d = await r?.json().catch(() => ({})) || {};
    if (r?.ok && d.ok) {
      pushToast(d.already ? 'Bereits als Ghost-Draft vorhanden' : 'Als Entwurf in Ghost angelegt ✓', 'success');
      loadIdeas();
    } else {
      pushToast(d.error || 'Ghost-Push fehlgeschlagen', 'error');
    }
    setBusy('');
  };

  // Loop-Closure: aus der Lücke (oder einem Faktencheck-Befund) einen Recherche-Job erzeugen
  const researchGap = async (id, query) => {
    setBusy(`rg-${id}`);
    const r = await fetch(`/api/ideas/${id}/research-gap`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(query ? { query } : {}),
    }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r && (r.ok || r.status === 202)) {
      pushToast(`🔍 Recherche-Job angelegt: „${(d.topic || '').slice(0, 55)}…" — läuft jetzt im Recherche-Tab`, 'success');
    } else {
      pushToast(d.error || 'Recherche nicht möglich', 'error');
    }
    setBusy('');
  };

  const generateRepurpose = async (id, channel) => {
    setBusy(`rp-${id}`);
    const r = await fetch(`/api/ideas/${id}/repurpose`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel }),
    }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r && (r.ok || r.status === 202)) {
      pushToast('Variante wird erstellt (~1 min)…', 'info');
      loadIdeas();
    } else {
      pushToast(d.error || 'Repurpose nicht möglich', r && (r.status === 409 || r.status === 400) ? 'info' : 'error');
    }
    setBusy('');
  };

  const generateVideo = async (id) => {
    setBusy(`vid-${id}`);
    const r = await fetch(`/api/ideas/${id}/video`, { method: 'POST', headers: auth }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r && (r.ok || r.status === 202)) {
      pushToast('🎬 Video wird erstellt — Skript lokal, Rendern bei revid.ai (einige Minuten)…', 'info');
      loadIdeas();
    } else {
      pushToast(d.error || 'Video nicht möglich', r && (r.status === 409 || r.status === 400) ? 'info' : 'error');
    }
    setBusy('');
  };

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => pushToast('In die Zwischenablage kopiert', 'success'),
        () => pushToast('Kopieren nicht möglich', 'error'));
    } else {
      pushToast('Zwischenablage nicht verfügbar', 'error');
    }
  };

  const setStatus = async (id, status) => {
    setBusy(`s-${id}`);
    const r = await fetch(`/api/ideas/${id}`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }).catch(() => null);
    if (r?.ok) { pushToast(`→ ${STATUS_META[status].label}`, 'info'); loadIdeas(); }
    else pushToast('Status-Wechsel fehlgeschlagen', 'error');
    setBusy('');
  };

  const saveNotes = async (id, notes) => {
    const r = await fetch(`/api/ideas/${id}`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    }).catch(() => null);
    if (r?.ok) pushToast('Notiz gespeichert', 'success');
  };

  const handleDelete = async (id) => {
    setBusy(`del-${id}`);
    const r = await fetch(`/api/ideas/${id}`, { method: 'DELETE', headers: auth }).catch(() => null);
    loadIdeas();
    setBusy('');
    // Audit v3: ehrlicher Toast — vorher hieß es auch bei Fehlschlag „gelöscht"
    if (r?.ok) pushToast('Idee gelöscht', 'info');
    else pushToast('Löschen fehlgeschlagen', 'error');
  };

  // Dispatcher für den "Nächster Schritt"-Button
  const runNextStep = (idea) => {
    const step = nextStep(idea);
    if (step.busy) return;
    switch (step.key) {
      case 'select':    return setStatus(idea.id, 'ausgewaehlt');
      case 'reopen':    return setStatus(idea.id, 'offen');
      case 'outline':   return generateOutline(idea.id);
      case 'draft':     return generateDraft(idea.id);
      case 'factcheck': return generateFactCheck(idea.id);
      case 'ghost':     return pushToGhost(idea.id);
      default:          return setOpenIdeaId(idea.id);
    }
  };

  const byStatus = (st) => ideas.filter(i => (i.status || 'offen') === st);

  // Verteil-Protokoll laden (inkl. Ghost-LIVE-Status draft/veröffentlicht).
  // Audit v3: Fehler nicht als „Noch nichts verteilt" maskieren; 401 → Logout.
  const loadDistribution = useCallback(() => {
    setDist(null);
    fetch('/api/ideas/distribution', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => {
        if (r.status === 401) { pushToast('Sitzung abgelaufen', 'error'); onLogout?.(); throw new Error('401'); }
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(d => setDist(d.items || []))
      .catch(() => { setDist('error'); pushToast('Verteil-Protokoll konnte nicht geladen werden', 'error'); });
  }, [authToken, onLogout]);

  const switchView = (v) => {
    setView(v);
    if (v === 'dist') loadDistribution();
  };

  const fmtD = iso => iso ? new Date(iso).toLocaleDateString('de-DE') : '—';
  const CHANNEL_ICON = { linkedin: '💼', newsletter: '✉', teaser: '⚡', video: '🎬' };
  const safeHttp = (u) => (u && /^https?:\/\//i.test(u) ? u : undefined);   // Audit v3

  // Globaler Busy-Chip: was rechnet gerade? (verhindert 409-Klickfrust)
  const runningJobs = ideas.flatMap(i => {
    const out = [];
    if (i.outline_status === 'generating')    out.push({ id: i.id, what: 'Gliederung', title: i.title });
    if (i.draft_status === 'generating')      out.push({ id: i.id, what: 'Entwurf', title: i.title });
    if (i.fact_check_status === 'generating') out.push({ id: i.id, what: 'Faktencheck', title: i.title });
    if (i.repurpose_status === 'generating')  out.push({ id: i.id, what: 'Repurpose', title: i.title });
    if (i.revid_status === 'rendering')       out.push({ id: i.id, what: 'Video-Render', title: i.title });
    return out;
  });

  const openIdea = ideas.find(i => i.id === openIdeaId) || null;

  return (
    <div className="ideas-view">
      <Toast messages={toasts} />

      {openIdea && (
        <IdeaModal
          idea={openIdea} busy={busy}
          onClose={() => setOpenIdeaId(null)}
          setStatus={setStatus}
          generateOutline={generateOutline}
          generateDraft={generateDraft}
          generateFactCheck={generateFactCheck}
          pushToGhost={pushToGhost}
          generateRepurpose={generateRepurpose}
          generateVideo={generateVideo}
          researchGap={researchGap}
          saveNotes={saveNotes}
          handleDelete={handleDelete}
          onDiscussIdea={onDiscussIdea}
          onOpenReport={setViewReport}
          copyText={copyText}
        />
      )}
      {viewReport !== null && (
        <ReportViewer reportId={viewReport} authToken={authToken} onClose={() => setViewReport(null)} />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Artikel-Ideen</h1>
          <p className="page-sub">Layer 2 — Synthese aus den Recherchen. Wähle Ideen für die Weiterbearbeitung.</p>
        </div>
        <div className="page-header-actions">
          {onHelp && (
            <button className="btn-ghost-sm help-btn" onClick={() => onHelp(view === 'dist' ? 'verteilen' : 'ideen-board')}
              title="Hilfe zu dieser Ansicht öffnen">❓</button>
          )}
          {runningJobs.length > 0 && (
            <button className="pipeline-busy-chip" onClick={() => setOpenIdeaId(runningJobs[0].id)}
              title={runningJobs.map(r => `${r.what}: ${r.title}`).join('\n')}>
              ⏳ {runningJobs[0].what} läuft: „{runningJobs[0].title.slice(0, 28)}…"
              {runningJobs.length > 1 && ` · +${runningJobs.length - 1}`}
            </button>
          )}
          <button className="btn-outline" onClick={loadIdeas} title="Ideen-Liste neu laden">↻ Aktualisieren</button>
        </div>
      </div>

      {view === 'dist' && (
        <div className="dist-view">
          {dist === null && <div className="knowledge-pending">Lade Verteil-Protokoll (inkl. Ghost-Live-Status)…</div>}
          {dist === 'error' && (
            <div className="empty-state-card">
              <div className="empty-icon">⚠</div>
              <p className="empty-title">Verteil-Protokoll nicht ladbar</p>
              <button className="btn-outline" onClick={loadDistribution} title="Erneut versuchen">↻ Erneut versuchen</button>
            </div>
          )}
          {Array.isArray(dist) && dist.length === 0 && (
            <div className="empty-state-card">
              <div className="empty-icon">📤</div>
              <p className="empty-title">Noch nichts verteilt</p>
              <p className="empty-sub">Sobald eine Idee zu Ghost, revid oder in einen Repurpose-Kanal geht, erscheint sie hier — mit Ziel, Status und Datum.</p>
            </div>
          )}
          {Array.isArray(dist) && dist.length > 0 && (
            <div className="dist-table" role="table" aria-label="Verteil-Protokoll">
              <div className="dist-row dist-head" role="row">
                <span>Thema</span><span>👻 Ghost</span><span>🎬 Video</span><span>♻ Kanäle</span>
              </div>
              {dist.map(it => (
                <div key={it.id} className="dist-row" role="row">
                  <button className="dist-title" onClick={() => { setView('board'); setOpenIdeaId(it.id); }}
                    title="Idee im Detail öffnen">{it.title}</button>
                  <span className="dist-cell">
                    {it.ghost_post_id ? (
                      <>
                        <span className={`dist-chip ${it.ghost_live_status === 'published' ? 'dist-chip-pub' : ''}`}
                          title={it.ghost_live_status === 'published'
                            ? `In Ghost VERÖFFENTLICHT${it.ghost_published_at ? ' am ' + fmtD(it.ghost_published_at) : ''}`
                            : it.ghost_live_status === 'draft' ? 'Liegt in Ghost als unveröffentlichter Draft'
                            : 'Ghost-Status gerade nicht abfragbar — übergeben als Draft'}>
                          {it.ghost_live_status === 'published' ? '✅ veröffentlicht' : it.ghost_live_status === 'draft' ? '📝 Draft' : '📝 übergeben'}
                        </span>
                        {safeHttp(it.ghost_url) && <a href={safeHttp(it.ghost_url)} target="_blank" rel="noopener noreferrer" title="In Ghost öffnen">öffnen ↗</a>}
                        <span className="dist-date">{fmtD(it.ghost_pushed_at)}</span>
                      </>
                    ) : <span className="dist-none">—</span>}
                  </span>
                  <span className="dist-cell">
                    {it.revid_video_url ? (
                      <>
                        <a href={safeHttp(it.revid_video_url)} target="_blank" rel="noopener noreferrer" title="Video ansehen (vor Veröffentlichung: Art.-50-Kennzeichnung)">ansehen ↗</a>
                        <span className="dist-date">{fmtD(it.video_rendered_at)}</span>
                      </>
                    ) : <span className="dist-none">—</span>}
                  </span>
                  <span className="dist-cell" title={(it.repurpose_channels || []).join(', ') || 'keine Text-Varianten erzeugt'}>
                    {(it.repurpose_channels || []).length
                      ? it.repurpose_channels.map(c => <span key={c} className="dist-ch">{CHANNEL_ICON[c] || c}</span>)
                      : <span className="dist-none">—</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'board' && ideas.length > 0 && (
        <div className="stats-row">
          {STATUS_ORDER.map(st => (
            <div key={st} className="stat-card">
              <span className="stat-value">{byStatus(st).length}</span>
              <span className="stat-label">{STATUS_META[st].label}</span>
            </div>
          ))}
        </div>
      )}

      {view === 'board' && ideas.length === 0 && (
        <div className="empty-state-card">
          <div className="empty-icon">💡</div>
          <p className="empty-title">Noch keine Ideen</p>
          <p className="empty-sub">Der Synthese-Bot (Recherche-Job „Ideen-Synthese") erzeugt sie wöchentlich aus den Reports.</p>
        </div>
      )}

      {view === 'board' && (
      <div className="ideas-columns">
        {STATUS_ORDER.map(st => {
          const col = byStatus(st);
          if (col.length === 0 && st === 'verworfen') return null;  // leere Verworfen-Spalte ausblenden
          return (
            <div key={st} className={`ideas-col ${STATUS_META[st].cls}`}>
              <div className="ideas-col-header">
                <span>{STATUS_META[st].icon} {STATUS_META[st].label}</span>
                <span className="ideas-col-count">{col.length}</span>
              </div>
              <p className="ideas-col-desc">{STATUS_META[st].desc}</p>
              <div className="ideas-col-body">
              {col.map(idea => {
                const step = nextStep(idea);
                const started = PROGRESS_STEPS.some(p => p.has(idea) || p.gen(idea));
                return (
                  <div key={idea.id} className="idea-card">
                    <div className="idea-card-head" onClick={() => setOpenIdeaId(idea.id)}
                      title="Details öffnen (Inhalte, Quellen, Verteilen)">
                      <span className="idea-title">{idea.title}</span>
                    </div>
                    {idea.hook && <p className="idea-hook idea-hook-clamp">{idea.hook}</p>}

                    <div className="idea-card-meta">
                      {(idea.batch_id || '').startsWith('kombi') && (
                        <span className="idea-kombi-badge" title="Themenkind aus einer Recherche-Kombination">⊕</span>
                      )}
                      {started && (
                        <span className="idea-progress" aria-label="Pipeline-Fortschritt">
                          {PROGRESS_STEPS.map(p => (
                            <span key={p.key}
                              className={`idea-progress-dot ${p.has(idea) ? 'idea-progress-on' : ''} ${p.gen(idea) ? 'idea-progress-gen' : ''}`}
                              title={`${p.label}: ${p.gen(idea) ? 'läuft…' : p.has(idea) ? 'vorhanden' : 'noch nicht'}`}>
                              {p.icon}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>

                    <div className="idea-card-actions">
                      <button className="idea-next-btn" disabled={!!busy || step.busy}
                        onClick={() => runNextStep(idea)} title={step.title || ''}>
                        {step.label}
                      </button>
                      <button className="btn-ghost-sm idea-details-btn" onClick={() => setOpenIdeaId(idea.id)}
                        title="Alle Details, Inhalte & Aktionen öffnen">Details</button>
                      {st === 'offen' && (
                        <button className="btn-ghost-sm" disabled={!!busy}
                          onClick={() => setStatus(idea.id, 'verworfen')} title="Idee verwerfen">✕</button>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
